import React from 'react';
import { Portal } from '@cobalt/react-portal-provider';
import { Heading } from '@cobalt/react-typography';
import CobaltModal, { Dialog } from '@cobalt/react-modal';
import Flex from '@cobalt/react-flex';
import Button from '@cobalt/react-button';
import Spinner from '@cobalt/react-spinner';
import Box from '@cobalt/react-box';
import Icon from '@cobalt/react-icon';
import classNames from 'classnames';

import { ModalProps as _ModalProps } from './interface';

import './styles.scss';

const cls = 'modal-pro';

const defaultProps = {
  prefixCls: 'cobalt',
  size: 'medium',
  okText: 'OK',
  cancelText: 'Cancel',
};

export type ModalProps = _ModalProps & Partial<typeof defaultProps>;

const Modal = (props: ModalProps) => {
  const { visible, size, prefixCls, className, title, footer, children, closable, style } = props;

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { onCancel } = props;
    onCancel?.(e);
  };

  const handleOk = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { onOk } = props;
    onOk?.(e);
  };

  // ============================= Render =============================
  const componentCls = `${prefixCls}-${cls}`;
  const mergeClassNames = classNames(`${componentCls}`, className);

  const renderFooter = () => {
    const { confirmLoading, okText, cancelText, confirmDisabled } = props;
    return (
      <Flex gap={3} alignX="end">
        <Button type="secondary" onClick={handleCancel}>
          {cancelText}
        </Button>
        <Button type="primary" disabled={confirmLoading || confirmDisabled} onClick={handleOk}>
          {confirmLoading && <Spinner className="button-loading" size="micro" />}
          {okText}
        </Button>
      </Flex>
    );
  };

  let closer: React.ReactNode;
  if (closable) {
    closer = (
      <Flex alignX="end" paddingBottom="2">
        <Icon
          className={`${componentCls}-close`}
          name="close"
          aria-label="Close"
          size="small"
          color="white"
          onClick={handleCancel}
        />
      </Flex>
    );
  }

  return (
    <Portal>
      <CobaltModal visible={visible} size={size}>
        {closer}
        <Dialog>
          <Flex direction="column" className={mergeClassNames} scrollable>
            <Box grow width="100%" height="100%" padding="6">
              <div className={`${componentCls}-content`} style={style}>
                {title && (
                  <div className={`${componentCls}-header`}>
                    <div className={`${componentCls}-title`}>
                      <Heading level={2}>{title}</Heading>
                    </div>
                  </div>
                )}
                <div className={`${componentCls}-body`}>{children}</div>
                <div className={`${componentCls}-footer`}>{footer === undefined ? renderFooter() : footer}</div>
              </div>
            </Box>
          </Flex>
        </Dialog>
      </CobaltModal>
    </Portal>
  );
};

Modal.defaultProps = defaultProps;

export default Modal;
